import React, { type HTMLAttributes } from 'react';
import { TouchTarget } from '@accure/ui/components/button/button-catalyst';

import { Link } from '@/components/ui/link';
import { cls } from '@/utils/cls';

export function Sidebar({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'nav'>) {
  return <nav {...props} className={cls(className, 'flex h-full flex-col')} />;
}

export function SidebarHeader({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={cls(
        className,
        'flex flex-col border-b border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
      )}
    />
  );
}

export function SidebarBody({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={cls(
        className,
        'flex flex-1 flex-col overflow-y-auto p-4 [&>[data-slot=section]+[data-slot=section]]:mt-6',
      )}
    />
  );
}

export function SidebarFooter({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={cls(
        className,
        'flex flex-col border-t border-zinc-950/5 p-4 dark:border-white/5 [&>[data-slot=section]+[data-slot=section]]:mt-2.5',
      )}
    />
  );
}

export function SidebarSection({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      data-slot="section"
      className={cls(className, 'flex flex-col gap-0.5')}
    />
  );
}

export function SidebarDivider({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'hr'>) {
  return (
    <hr
      {...props}
      className={cls(
        className,
        'my-4 border-t border-zinc-950/5 dark:border-white/5 lg:-mx-4',
      )}
    />
  );
}

export function SidebarSpacer({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={cls(className, 'mt-8 flex-1')}
    />
  );
}

export function SidebarHeading({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'h3'>) {
  return (
    <h3
      {...props}
      className={cls(
        className,
        'mb-1 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400',
      )}
    />
  );
}

export const SidebarItem = React.forwardRef(function SidebarItem(
  {
    current,
    className,
    children,
    ...props
  }: { current?: boolean; className?: string; children: React.ReactNode } & (
    | Omit<HTMLAttributes<HTMLButtonElement>, 'className'>
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'type' | 'className'>
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const classes = cls(
    // Base
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 text-zinc-950 sm:py-2',
    // Leading icon/icon-only
    'data-[slot=icon]:*:size-6 data-[slot=icon]:*:shrink-0 data-[slot=icon]:*:fill-zinc-500 sm:data-[slot=icon]:*:size-5',
    // Leading icon/icon-only
    'data-[slot=outline-icon]:*:size-6 data-[slot=outline-icon]:*:shrink-0 data-[slot=outline-icon]:*:text-zinc-500 sm:data-[slot=outline-icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'data-[slot=icon]:last:*:ml-auto data-[slot=icon]:last:*:size-5 sm:data-[slot=icon]:last:*:size-4',
    // Avatar
    'data-[slot=avatar]:*:-m-0.5 data-[slot=avatar]:*:size-7 data-[slot=avatar]:*:[--ring-opacity:10%] sm:data-[slot=avatar]:*:size-6',
    // Hover
    'hover:bg-zinc-950/5 data-[slot=icon]:*:hover:fill-zinc-950',
    // Active
    'active:bg-zinc-950/5 data-[slot=icon]:*:active:fill-zinc-950',
    // Current
    'data-[slot=icon]:*:data-[current]:fill-zinc-950 data-[current]:bg-zinc-950/5',
    // Dark mode
    'dark:text-white dark:data-[slot=icon]:*:fill-zinc-400',
    'dark:hover:bg-white/5 dark:data-[slot=icon]:*:hover:fill-white',
    'dark:active:bg-white/5 dark:data-[slot=icon]:*:active:fill-white',
    'dark:data-[slot=icon]:*:data-[current]:fill-white dark:data-[current]:bg-white/5',
  );

  return (
    <span className={cls(className, 'relative')}>
      {current && (
        <span
          className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-zinc-950 transition-all duration-300 dark:bg-white"
          style={{
            transform: current ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'left',
          }}
        />
      )}
      {'to' in props ? (
        <Link
          className={classes}
          {...props}
          data-current={current ? 'true' : undefined}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <button
          {...props}
          className={cls('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref as React.RefObject<HTMLButtonElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </button>
      )}
    </span>
  );
});

export function SidebarLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={cls(className, 'truncate')} />;
}
